import type { DefaultValues, SubmitHandler } from 'react-hook-form';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type {
  IApiGetInvitationResponseBody,
  IApiInvitationLanguage,
} from '~/lib/api/modules/Invitations';
import type { IOrganizationInvitationFormValues } from './OrganizationInvitationForm';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import {
  IUseCreateInvitationMutationValues,
  useCreateInvitationMutation,
} from '~/lib/api/modules/Invitations/hooks/useCreateInvitationMutation';
import { OrganizationInvitationForm } from './OrganizationInvitationForm';

export type ICreateOrganizationInvitationFormProps = IContainerProps & {
  onSuccess?: (
    outputData: IApiGetInvitationResponseBody,
    inputValues: IUseCreateInvitationMutationValues,
  ) => void;
  cancelUrl?: string;
  defaultValues?: DefaultValues<IOrganizationInvitationFormValues>;
};

export const CreateOrganizationInvitationForm: React.FC<
  ICreateOrganizationInvitationFormProps
> = (props) => {
  const { sx, onSuccess, cancelUrl, defaultValues } = props;
  const createInvitationMutation = useCreateInvitationMutation();
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const handleSubmit: SubmitHandler<IOrganizationInvitationFormValues> = (
    values,
  ) => {
    if (createInvitationMutation.isLoading) {
      return;
    }

    return createInvitationMutation.mutateAsync(
      {
        body: {
          target: {
            type: 'organization',
            organizationId: values.organizationId!,
            role: values.role ?? 'member',
          },
          method: {
            type: 'email',
            emailAddress: values.email,
          },
          // TODO: the expiration date should rather be set in the backend
          expiredAt: DateTime.now().plus({ days: 30 }).toUTC().toISO(),
          toUserLocale: values.language,
        },
      },
      { onSuccess },
    );
  };

  return (
    <ContentBlock sx={sx}>
      <ContentBlock.Title
        supportingText={t('CreateOrganizationInvitationForm.headline')}
      >
        {t('CreateOrganizationInvitationForm.title')}
      </ContentBlock.Title>

      <OrganizationInvitationForm
        mode="creation"
        cancelUrl={cancelUrl}
        onSubmit={handleSubmit}
        defaultValues={{
          ...defaultValues,
          language: locale as IApiInvitationLanguage,
          role: 'admin',
        }}
      />
    </ContentBlock>
  );
};
